/**
 * The map centre used before, or instead of, the visitor's own location.
 *
 * Three surfaces need a starting point: the landing page's upcoming-events preview, which never
 * prompts for location because asking a signed-out visitor for it is hostile; the Discover map,
 * until the visitor asks to be located; and the submit-event picker.
 *
 * This should be the launch metro. Pointing it somewhere with no listings makes a populated
 * database look empty, which is indistinguishable from the app being broken.
 */

/** A geographic centre in degrees, with a name to show the user. */
export interface GeoCentre {
  /** Latitude in degrees. */
  readonly lat: number;
  /** Longitude in degrees. */
  readonly lon: number;
  /** A short place name, shown as "Showing events near {label}". */
  readonly label: string;
}

/** Downtown Miami — the launch metro. */
const FALLBACK_CENTRE: GeoCentre = { lat: 25.7617, lon: -80.1918, label: 'Miami' };

/**
 * Parses a coordinate from the Vite environment.
 *
 * @param raw - The raw environment value.
 * @param limit - The maximum absolute value (90 for latitude, 180 for longitude).
 * @returns The parsed coordinate, or `null` when absent or out of range.
 */
function parseCoordinate(raw: string | undefined, limit: number): number | null {
  if (raw === undefined || raw.trim() === '') {
    return null;
  }

  const value = Number(raw);

  return Number.isFinite(value) && Math.abs(value) <= limit ? value : null;
}

/**
 * Resolves the default map centre, allowing a deployment to move the launch metro without a code
 * change. Both coordinates must be set and valid for the override to apply; a partial or malformed
 * pair falls back rather than centring the map on the equator.
 *
 * @returns The configured centre, or the built-in launch metro.
 */
export function defaultCentre(): GeoCentre {
  const lat = parseCoordinate(import.meta.env['VITE_DEFAULT_CENTRE_LAT'] as string | undefined, 90);
  const lon = parseCoordinate(import.meta.env['VITE_DEFAULT_CENTRE_LON'] as string | undefined, 180);

  if (lat === null || lon === null) {
    return FALLBACK_CENTRE;
  }

  const label = (import.meta.env['VITE_DEFAULT_CENTRE_LABEL'] as string | undefined)?.trim();

  return { lat, lon, label: label !== undefined && label !== '' ? label : 'the area' };
}
